import React from 'react'
import * as rn from 'react-native'

import * as c from '../../../common'
import { coverPickerCompanies } from '../../../common/helpers/coverCompanies'
import {
  coverFollowsContract,
  resolveCoverLogo,
  signedBeforeHomeCamCover,
} from '../../../common/helpers/coverLogo'
import { reportContractLogo } from '../../../common/helpers/reportContractLogo'
import { contractCompaniesForPicker } from '../../../common/helpers/retiredContractCompanies'
import {
  coverHasSignature,
  hasAnySignature,
} from '../../../common/helpers/signingFlowV2'
import * as r from '../../../react-utils'

import * as m from '@mui/material'
import Entypo from 'react-native-vector-icons/Entypo'

import * as gs from '../../../app/gStyles'
import * as cpm from '../../../app/components'
// HOMENOW is green everywhere (Luis Oct 30, same as app icon/login; was gray since Sep 28)
import homenowGrayLogo from '../../img/homenow-green.png'
import roofnowTrimmedLogo from '../../img/roofnow-trimmed.png'
import { CompanySelectorReadyContext } from './companySelectorLoad'
import { COVER_ONLY_LOGOS } from './coverCompanyLogos'

/**
 * Everything the report cover needs to decide which logo to print. Shared by
 * `MainPageReport` (to know whether the cover has its own selector at all) and
 * by `CoverLogo` itself.
 */
export const useCoverLogo = (customerID: string) => {
  const {
    contractCompaniesAllStates,
    contractCompaniesCurrStateArr,
    contractCompanyShortName,
    handleCoverCompany,
  } = r.useContractCompany(customerID)
  const [customerData] = r.useCustomer(customerID)
  const coverCompany = customerData.coverCompany

  const logos = React.useMemo(() => {
    const fromContracts: Record<string, string | undefined> = {}
    for (const company of [
      ...c.values(contractCompaniesAllStates),
      ...contractCompaniesCurrStateArr,
    ]) {
      fromContracts[company.shortName] = reportContractLogo(
        company.shortName,
        company.logo,
        homenowGrayLogo,
        roofnowTrimmedLogo,
      )
    }
    return { ...COVER_ONLY_LOGOS, ...fromContracts }
  }, [contractCompaniesAllStates, contractCompaniesCurrStateArr])

  const pickerCompanies = React.useMemo(
    () =>
      coverPickerCompanies(
        // The synthetic "Other" entries (V44) are contract-only: on the cover
        // they would print generic text instead of a brand.
        contractCompaniesForPicker(
          contractCompaniesCurrStateArr.filter(
            (company) =>
              company.shortName !== r.OTHER_CASH_SHORT_NAME &&
              company.shortName !== r.OTHER_FIN_SHORT_NAME,
          ),
          coverCompany,
        ),
        COVER_ONLY_LOGOS,
      ).map((company) => ({
        ...company,
        // The row shows the logo the cover would print: where ROOFNOW is a
        // contract company (California) Firebase has it with wide margins.
        logo: logos[company.shortName] || company.logo,
      })),
    [contractCompaniesCurrStateArr, coverCompany, logos],
  )

  const signed = coverHasSignature(customerData)
  const coverLogo = resolveCoverLogo({
    coverCompany,
    hcLogo: contractCompaniesAllStates['HC']?.logo,
    logos,
    shortName: contractCompanyShortName,
    signed,
    signedBefore: signedBeforeHomeCamCover([
      [
        customerData.security_contract_sign_customer,
        customerData.security_contract_sign_customer_date,
      ],
      [
        customerData.security_contract_sign_company,
        customerData.security_contract_sign_company_date,
      ],
      [
        customerData.security_contract_cancel_sign_customer,
        customerData.security_contract_cancel_sign_customer_date,
      ],
      [
        customerData.home_improvement_sign_owner1,
        customerData.home_improvement_sign_owner1_date,
      ],
      [
        customerData.home_improvement_sign_owner2,
        customerData.home_improvement_sign_owner2_date,
      ],
      [
        customerData.home_improvement_sign_company,
        customerData.home_improvement_sign_company_date,
      ],
      [
        customerData.home_improvement_confirm_equipment_sign,
        customerData.home_improvement_confirm_equipment_sign_date,
      ],
      [
        customerData.home_improvement_confirm_total_initials,
        customerData.home_improvement_confirm_total_initials_date,
      ],
      [
        customerData.home_improvement_cancel_sign_owner1,
        customerData.home_improvement_cancel_sign_owner1_date,
      ],
      [
        customerData.home_improvement_cancel_sign_owner2,
        customerData.home_improvement_cancel_sign_owner2_date,
      ],
    ]),
  })

  // What "nothing picked" prints for this customer: the option's name and the
  // logo's alt must not say HomeCam when the cover is following the contract.
  const defaultCoverName = coverFollowsContract({
    coverCompany: '',
    shortName: contractCompanyShortName,
    signed,
  })
    ? contractCompaniesAllStates[contractCompanyShortName]?.longName
    : undefined

  return {
    coverCompany,
    coverLogo,
    defaultCoverName: defaultCoverName || 'HomeCam',
    handleCoverCompany,
    /** Once any signature exists the cover is frozen (V67). */
    locked: hasAnySignature(customerData),
    pickerCompanies,
    /**
     * False only in the legacy case (nothing picked, nothing derived): the
     * cover then keeps its old behavior and has no selector of its own.
     */
    showCover: !!coverCompany || !!coverLogo,
  } as const
}

export type CoverLogoProps = c.r<{
  customerID: string
  /** Class of the logo `<img>`, so the cover keeps its own sizing. */
  imgClassName?: string
  isPDF?: boolean
  /**
   * V43-style gate: must be true during public token signing (?mode=sign).
   * It also stops the Pressable from opening the picker at all.
   */
  readOnly?: boolean
}>

export default React.memo<CoverLogoProps>(function CoverLogo({
  customerID,
  imgClassName,
  isPDF,
  readOnly,
}) {
  const {
    coverCompany,
    coverLogo,
    defaultCoverName,
    handleCoverCompany,
    locked: signed,
    pickerCompanies,
  } = useCoverLogo(customerID)
  // Not before the customer is loaded (companySelectorLoad.ts).
  const selectorReady = React.useContext(CompanySelectorReadyContext)
  const locked = !!readOnly || signed || !selectorReady

  const [choosingCompany, , toggleChoosingCompany] = r.useBool(false)

  const chosenName =
    pickerCompanies.find(({ shortName }) => shortName === coverCompany)
      ?.longName || coverCompany

  return (
    <>
      <rn.Pressable
        onPress={locked ? undefined : toggleChoosingCompany}
        style={gs.rowCentered}
      >
        {coverLogo ? (
          <img
            alt={coverCompany ? chosenName : `${defaultCoverName} logotype`}
            className={imgClassName}
            src={coverLogo}
          />
        ) : (
          <cpm.Text colorScheme="light">{chosenName}</cpm.Text>
        )}

        {!isPDF && !locked && (
          <>
            {gs.colGap8}

            <Entypo
              color={c.light.canvas.opaque}
              name="select-arrows"
              size={18}
              style={gs.alignSelfEnd}
            />
          </>
        )}
      </rn.Pressable>

      <m.Select
        onChange={locked ? undefined : handleCoverCompany}
        onClose={toggleChoosingCompany}
        onOpen={toggleChoosingCompany}
        open={!locked && choosingCompany}
        renderValue={c.alwaysNull}
        style={sx.select}
        value={coverCompany}
      >
        {/* The way back to the default cover, whichever company that is here. */}
        <m.MenuItem key="" style={sx.menuItem} value="">
          <cpm.Text colorScheme="light" size={18}>
            Default ({defaultCoverName} logo)
          </cpm.Text>
        </m.MenuItem>

        {pickerCompanies.map(({ logo, longName, shortName }) => (
          <m.MenuItem key={shortName} style={sx.menuItem} value={shortName}>
            {logo && (
              <img
                alt={longName}
                src={logo}
                style={{
                  // Same 100x40 box as the contract picker, also for a
                  // portrait logo (ESR): see ContractLogo's pickerLogo.
                  flexShrink: 0,
                  height: 40,
                  objectFit: 'contain',
                  width: 100,
                }}
              />
            )}

            <rn.View style={gs.rowCentered}>
              {gs.colGap8}
              <cpm.Text colorScheme="light" size={18}>
                {longName}
              </cpm.Text>
            </rn.View>

            {gs.rowGap8}
          </m.MenuItem>
        ))}
      </m.Select>
    </>
  )
})

const sx = {
  menuItem: {
    alignItems: 'stretch',
    flexDirection: 'row',
    paddingBottom: 24,
    paddingTop: 24,
  } as const,
  select: { ...gs.width72, height: 0, opacity: 0 },
}
